/**
 * Листание списка записей при неполном счёте поиска (#930).
 *
 * Сервер собирает выдачу поиска порциями id и прямо говорит, когда `total` —
 * нижняя граница (`searchTotalIsPartial`). Остановить прокрутку на
 * `loaded === total` значит вернуть ту самую стену, которую сервер снял:
 * записи за первой порцией так и не будут запрошены.
 */
import { formatNumber } from '@/lib/utils';

interface RecordsPageShape {
  records: readonly unknown[];
  total: number;
  searchTotalIsPartial?: boolean;
  /** Счёт остановлен потолком (#3193): `total` — нижняя граница, не конец списка. */
  totalIsCapped?: boolean;
  /** Курсор порций поиска (#980) — непрозрачная строка сервера. */
  searchCursor?: string;
  searchFallback?: string;
}

/**
 * Смещение следующей страницы или `undefined`, если листать дальше нечего.
 *
 * При неполном счёте продолжаем, пока страница приносит записи. Пустая
 * неполная страница — конец: сервер исчерпал бюджет порций на этой глубине,
 * и повтор того же смещения вернёт то же самое — без этого условия прокрутка
 * крутилась бы в цикле запросов.
 */
export function nextRecordsOffset(
  lastPage: RecordsPageShape,
  allPages: readonly RecordsPageShape[],
  options: {
    /**
     * Серия звонков — одна страница по контракту репозитория: при `seriesId`
     * он игнорирует смещение и отдаёт до 100 записей с начала. Следующая
     * «страница» вернула бы те же строки — дубли и повторные запросы.
     */
    singlePage?: boolean;
  } = {}
): number | undefined {
  if (options.singlePage) return undefined;
  // Пустая страница — конец всегда: она не сдвинула смещение, и повтор вернёт
  // её же. Раньше это держал только `loaded < total`, а при неполном счёте
  // или сервере, игнорирующем смещение, такого стража нет.
  if (lastPage.records.length === 0) return undefined;
  const loaded = allPages.reduce((sum, page) => sum + page.records.length, 0);
  if (lastPage.searchTotalIsPartial || lastPage.totalIsCapped) return loaded;
  return loaded < lastPage.total ? loaded : undefined;
}

/** Параметр страницы бесконечного списка: смещение и, при поиске, курсор. */
export interface RecordsPageParam {
  offset: number;
  searchCursor?: string;
}

/**
 * Следующая страница — с курсором порций, если сервер его дал (#980).
 *
 * С курсором сервер тратит бюджет порций от него, а не от начала совпадений,
 * поэтому листать можно на любую глубину. Пустая страница с курсором — НЕ
 * конец: набор мог пройти порции, целиком отсеянные правами, и сдвинул курсор.
 * Конец — ответ без курсора (тогда прежняя логика по `offset`) или курсор, не
 * сдвинувшийся с прошлого ответа: повтор дал бы то же самое.
 */
export function nextRecordsPageParam(
  lastPage: RecordsPageShape,
  allPages: readonly RecordsPageShape[],
  options: { singlePage?: boolean } = {}
): RecordsPageParam | undefined {
  if (options.singlePage) return undefined;
  const loaded = allPages.reduce((sum, page) => sum + page.records.length, 0);
  if (lastPage.searchCursor) {
    const previous = allPages.length > 1 ? allPages[allPages.length - 2] : undefined;
    if (previous?.searchCursor === lastPage.searchCursor) return undefined;
    return { offset: loaded, searchCursor: lastPage.searchCursor };
  }
  const offset = nextRecordsOffset(lastPage, allPages, options);
  return offset === undefined ? undefined : { offset };
}

/**
 * Счёт и его полнота — с ПОСЛЕДНЕЙ загруженной страницы.
 *
 * При поиске порциями они не постоянны: более глубокая страница просматривает
 * больше порций, и её `total` больше, а на последней порции признак
 * неполноты снимается. Счёт первой страницы дал бы «2050 / 2000» и пометку
 * «не меньше 2000» уже после того, как сервер установил точные 2500.
 */
export function recordsTotalState(pages: readonly RecordsPageShape[] | undefined): {
  total: number;
  partial: boolean;
  /** Показать «N+»: счёт неполон из-за поиска или остановлен потолком (#3193). */
  lowerBound: boolean;
} {
  const last = pages?.[pages.length - 1];
  const partial = last?.searchTotalIsPartial ?? false;
  return {
    total: last?.total ?? 0,
    partial,
    lowerBound: partial || (last?.totalIsCapped ?? false),
  };
}

/**
 * Счёт для показа: «1 500+», когда он нижняя граница (#1107).
 *
 * Плашка под поиском говорит «не меньше 1 500», и голое «1500» в заголовке
 * рядом с ней читалось как точное число.
 */
export function formatRecordsTotal({
  total,
  partial,
}: {
  total: number;
  partial: boolean;
}): string {
  return partial ? `${formatNumber(total)}+` : formatNumber(total);
}

/**
 * Сколько коротких страниц с курсором подряд хук догружает сам (#1048).
 *
 * Подгрузка по прокрутке срабатывает, только когда список прокручивается. Но
 * при поиске порциями страница бывает короткой или пустой: набор прошёл
 * порции, почти целиком отсеянные правами, а совпадения ещё не кончились.
 * Пустой список не прокручивается, и агент, чьи записи лежат глубже, так и
 * видел бы «ничего не найдено». Поэтому такие страницы догружаются сами — но
 * не бесконечно: дальше решает человек кнопкой «Искать дальше».
 */
export const RECORDS_AUTO_CONTINUE_LIMIT = 5;

/** Сколько последних страниц подряд — короткие страницы поиска с курсором. */
function trailingShortCursorPages(
  pages: readonly RecordsPageShape[],
  pageSize: number
): number {
  let n = 0;
  for (let i = pages.length - 1; i >= 0; i -= 1) {
    const page = pages[i]!;
    if (!page.searchCursor || page.records.length >= pageSize) break;
    n += 1;
  }
  return n;
}

/**
 * Состояние «поиск идёт глубже»:
 * - `continue` — догрузить следующую страницу самим (страница короткая, курсор есть);
 * - `stalled` — предел автодогрузки исчерпан, нужна кнопка «Искать дальше»;
 * - `idle` — ничего делать не надо.
 *
 * Счёт — по хвосту коротких страниц, поэтому нажатие кнопки даёт ещё одну
 * страницу, а следующая короткая снова остановится до нового нажатия.
 */
export function recordsSearchDeeperState(
  pages: readonly RecordsPageShape[] | undefined,
  options: {
    hasNextPage: boolean;
    pageSize: number;
    /**
     * Прошлая догрузка упала. Тогда — `stalled`: кнопка становится повтором.
     * Иначе эффект повторял бы упавший запрос без конца — страниц не
     * прибавляется, и предел, считающий страницы, его не остановит.
     */
    fetchNextPageFailed?: boolean;
  }
): 'continue' | 'stalled' | 'idle' {
  const last = pages?.[pages.length - 1];
  if (!pages || !last || !options.hasNextPage) return 'idle';
  if (!last.searchCursor || last.records.length >= options.pageSize) return 'idle';
  if (options.fetchNextPageFailed) return 'stalled';
  return trailingShortCursorPages(pages, options.pageSize) >= RECORDS_AUTO_CONTINUE_LIMIT
    ? 'stalled'
    : 'continue';
}

/**
 * Часть выдачи дал запасной D1 LIKE, потому что Postgres был недоступен (#1035).
 *
 * По ВСЕМ загруженным страницам, а не по первой или последней: каждая страница
 * — свой запрос, и при прокрутке первая может прийти из Postgres, а следующая
 * — из D1. Движки расходятся на регистре вне A–Z, поэтому достаточно одной
 * такой страницы, чтобы список на экране стал неполным без объяснения.
 *
 * `short_query` пометки не даёт намеренно: однобуквенный запрос предсказуем и
 * описан в документации, а пометка на каждую букву стала бы шумом.
 */
export function recordsSearchDegraded(
  pages: readonly RecordsPageShape[] | undefined
): boolean {
  return pages?.some((page) => page.searchFallback === 'pg_unavailable') ?? false;
}
